import {
  DFC_CORE_LANGUAGES,
  DIALECT_CODES,
  DialectKeyValue,
  ENABLED_EVALUATOR_DIALECTS,
  ENABLED_PROFILE_DIALECTS,
  IDTL_SUPPORTED_LANGUAGES,
} from 'variables';
import { EventType, LicensedProduct } from 'types/types';

export enum Experience {
  IDTL,
  DISCOVERY,
  DFC,
  EXPLORE,
}

export const eventTypeToExperience: Record<EventType, Experience> = {
  INSIGHTS_SELF_AWARE_LEADER: Experience.IDTL,
  INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS: Experience.IDTL,
  INSIGHTS_DISCOVERY_WORKSHOP: Experience.DISCOVERY,
  INSIGHTS_DISCOVERY_ACCREDITATION: Experience.DISCOVERY,
  INSIGHTS_DISCOVERY_FULL_CIRCLE: Experience.DFC,
  INSIGHTS_EXPLORE: Experience.EXPLORE,
  INSIGHTS_CRITICAL_THINKING: Experience.DISCOVERY,
};

export const isIdtlExperience = (eventType: EventType | '') =>
  eventTypeToExperience[eventType as EventType] === Experience.IDTL;

export const isDiscoveryExperience = (eventType: EventType | '') =>
  eventTypeToExperience[eventType as EventType] === Experience.DISCOVERY;

export const isDFCExperience = (eventType: EventType | '') =>
  eventTypeToExperience[eventType as EventType] === Experience.DFC;

export const isExploreExperience = (eventType: EventType | '') =>
  eventTypeToExperience[eventType as EventType] === Experience.EXPLORE;

export const experienceSupportsChapters = (eventType: EventType) =>
  !(isIdtlExperience(eventType) || isDFCExperience(eventType));

export const experienceSupportsDetails = (eventType: EventType) =>
  !isDFCExperience(eventType);

export const experienceSupportsLocation = (eventType: EventType) =>
  !isDFCExperience(eventType);

export const getEventDialects = (event: {
  eventType: EventType | '';
}): Array<DialectKeyValue> =>
  isIdtlExperience(event.eventType)
    ? [
        {
          textKey:
            'ui.event-management.events.create.event-language.english_united_kingdom',
          value: DIALECT_CODES.ENGLISH,
        },
      ]
    : ENABLED_PROFILE_DIALECTS.map((dialect) => {
        return {
          textKey: dialect.textKey,
          value: dialect.value,
        };
      });

export const getEvaluatorDialects = (event: {
  eventType: EventType;
  productType: LicensedProduct;
}) => {
  if (isIdtlExperience(event.eventType)) {
    return IDTL_SUPPORTED_LANGUAGES;
  }
  if (isDFCExperience(event.eventType)) {
    return DFC_CORE_LANGUAGES;
  }
  return ENABLED_EVALUATOR_DIALECTS.filter(
    (dialect) => !dialect.disabledForProducts?.includes(event.productType),
  );
};

export const canChangeEventType = (eventType: EventType | '') =>
  [
    'INSIGHTS_DISCOVERY_WORKSHOP',
    'INSIGHTS_DISCOVERY_ACCREDITATION',
    'INSIGHTS_SELF_AWARE_LEADER',
    'INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS',
  ].includes(eventType);

const COMPATIBLE_EVENT_TYPES: Record<EventType, EventType[]> = {
  INSIGHTS_DISCOVERY_WORKSHOP: ['INSIGHTS_DISCOVERY_ACCREDITATION'],
  INSIGHTS_DISCOVERY_ACCREDITATION: ['INSIGHTS_DISCOVERY_WORKSHOP'],
  INSIGHTS_SELF_AWARE_LEADER: ['INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS'],
  INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS: ['INSIGHTS_SELF_AWARE_LEADER'],
  INSIGHTS_DISCOVERY_FULL_CIRCLE: [],
  INSIGHTS_EXPLORE: [],
  INSIGHTS_CRITICAL_THINKING: [],
};

export const eventTypesAreCompatible = (
  eventType1: EventType,
  eventType2: EventType,
) => {
  if (eventType1 === eventType2) {
    return true;
  }
  return COMPATIBLE_EVENT_TYPES[eventType1].includes(eventType2);
};
